// P3-020: Enemy battle visual — placeholder rectangle, HP bar, hit animation
// Demand-change visual indicator

import Phaser from 'phaser';
import { GAME_DPR } from '@/shared/game/main';

const ENEMY_WIDTH = 200;
const ENEMY_HEIGHT = 80;
const HP_BAR_HEIGHT = 10;
const HP_BAR_WIDTH = 180;
const HP_BAR_Y = ENEMY_HEIGHT / 2 + 12;

export class EnemySprite extends Phaser.GameObjects.Container {
  private bg: Phaser.GameObjects.Graphics;
  private nameLabel: Phaser.GameObjects.Text;
  private hpBar: Phaser.GameObjects.Graphics;
  private hpBarBg: Phaser.GameObjects.Graphics;
  private hpText: Phaser.GameObjects.Text;
  private statusLabel: Phaser.GameObjects.Text;
  private demandOverlay: Phaser.GameObjects.Graphics;

  private currentHp = 0;
  private maxHp = 0;
  private displayedHp = 0; // For animated HP drain
  private hpTween: Phaser.Tweens.Tween | null = null;
  private baseY = 0; // Original Y position for bounce animation

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    name: string,
    maxHp: number,
  ) {
    super(scene, x, y);

    this.currentHp = maxHp;
    this.maxHp = maxHp;
    this.displayedHp = maxHp;
    this.baseY = y;

    // Background body
    this.bg = scene.add.graphics();
    this.bg.fillStyle(0x2a2824, 0.9);
    this.bg.fillRoundedRect(-ENEMY_WIDTH / 2, -ENEMY_HEIGHT / 2, ENEMY_WIDTH, ENEMY_HEIGHT, 8);
    this.bg.lineStyle(2, 0xe05545, 0.6);
    this.bg.strokeRoundedRect(-ENEMY_WIDTH / 2, -ENEMY_HEIGHT / 2, ENEMY_WIDTH, ENEMY_HEIGHT, 8);
    this.add(this.bg);

    // Project name
    this.nameLabel = scene.add.text(0, -16, name, {
      fontFamily: 'JetBrains Mono, Consolas, monospace',
      fontSize: '14px',
      color: '#e8e4dc',
      fontStyle: 'bold',
      resolution: GAME_DPR,
    });
    this.nameLabel.setOrigin(0.5, 0.5);
    this.add(this.nameLabel);

    // "PROJECT" subtitle
    const subtitle = scene.add.text(0, 4, '// PROJECT', {
      fontFamily: 'JetBrains Mono, Consolas, monospace',
      fontSize: '10px',
      color: '#6b6459',
      resolution: GAME_DPR,
    });
    subtitle.setOrigin(0.5, 0.5);
    this.add(subtitle);

    // HP bar background
    this.hpBarBg = scene.add.graphics();
    this.hpBarBg.fillStyle(0x171613, 1);
    this.hpBarBg.fillRoundedRect(-HP_BAR_WIDTH / 2, HP_BAR_Y, HP_BAR_WIDTH, HP_BAR_HEIGHT, 3);
    this.hpBarBg.lineStyle(1, 0x3a362e, 1);
    this.hpBarBg.strokeRoundedRect(-HP_BAR_WIDTH / 2, HP_BAR_Y, HP_BAR_WIDTH, HP_BAR_HEIGHT, 3);
    this.add(this.hpBarBg);

    // HP bar fill
    this.hpBar = scene.add.graphics();
    this.add(this.hpBar);
    this.drawHpBar();

    // HP text
    this.hpText = scene.add.text(0, HP_BAR_Y + HP_BAR_HEIGHT + 10, '', {
      fontFamily: 'JetBrains Mono, Consolas, monospace',
      fontSize: '10px',
      color: '#9b9488',
      resolution: GAME_DPR,
    });
    this.hpText.setOrigin(0.5, 0.5);
    this.add(this.hpText);
    this.updateHpText();

    // Status label (for demand-change)
    this.statusLabel = scene.add.text(0, -ENEMY_HEIGHT / 2 - 14, '', {
      fontFamily: 'JetBrains Mono, Consolas, monospace',
      fontSize: '10px',
      color: '#e6b832',
      resolution: GAME_DPR,
    });
    this.statusLabel.setOrigin(0.5, 0.5);
    this.statusLabel.setVisible(false);
    this.add(this.statusLabel);

    // Demand change overlay
    this.demandOverlay = scene.add.graphics();
    this.demandOverlay.setAlpha(0);
    this.add(this.demandOverlay);

    scene.add.existing(this);
  }

  /** Draw the HP bar fill based on displayed HP (for animation) */
  private drawHpBar(): void {
    this.hpBar.clear();
    const ratio = this.maxHp > 0 ? Math.max(0, this.displayedHp / this.maxHp) : 0;
    const fillWidth = Math.round(HP_BAR_WIDTH * ratio);

    // Color based on HP ratio
    let color = 0x52c07a; // green
    if (ratio <= 0.33) {
      color = 0xe05545; // red
    } else if (ratio <= 0.66) {
      color = 0xe6b832; // yellow
    }

    if (fillWidth > 0) {
      this.hpBar.fillStyle(color, 1);
      this.hpBar.fillRoundedRect(-HP_BAR_WIDTH / 2, HP_BAR_Y, fillWidth, HP_BAR_HEIGHT, 3);
    }
  }

  /** Update HP text label */
  private updateHpText(): void {
    const percent = this.maxHp > 0 ? Math.round((this.currentHp / this.maxHp) * 100) : 0;
    this.hpText.setText(`HP: ${Math.max(0, Math.round(this.currentHp))}/${this.maxHp} [${percent}%]`);
  }

  /** Update HP with animated drain effect (600ms tween) */
  setHp(hp: number): void {
    const newHp = Math.max(0, hp);
    this.currentHp = newHp;

    // Cancel any existing HP tween
    if (this.hpTween) {
      this.hpTween.stop();
    }

    // Animate the HP bar drain
    this.hpTween = this.scene.tweens.add({
      targets: this,
      displayedHp: newHp,
      duration: 600,
      ease: 'Sine.easeOut',
      onUpdate: () => {
        this.drawHpBar();
        this.updateHpText();
      },
      onComplete: () => {
        this.displayedHp = newHp;
        this.drawHpBar();
        this.updateHpText();
        this.hpTween = null;
      },
    });
  }

  /** Play enhanced hit animation (shake + Y bounce + scale pulse + flash) */
  playHitAnimation(comboCount = 1): void {
    // X shake
    this.scene.tweens.add({
      targets: this,
      x: { from: this.x - 5, to: this.x + 5 },
      duration: 40,
      repeat: 3,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        this.x = Math.round(this.x);
      },
    });

    // Y bounce (-5px then back)
    this.scene.tweens.add({
      targets: this,
      y: this.baseY - 5,
      duration: 80,
      ease: 'Back.easeOut',
      yoyo: true,
      onComplete: () => {
        this.y = this.baseY;
      },
    });

    // Scale pulse (1.05x then back)
    this.scene.tweens.add({
      targets: this,
      scaleX: 1.05,
      scaleY: 1.05,
      duration: 100,
      ease: 'Back.easeOut',
      yoyo: true,
    });

    // Brief red flash
    const flash = this.scene.add.graphics();
    flash.fillStyle(0xe05545, 0.35);
    flash.fillRoundedRect(-ENEMY_WIDTH / 2, -ENEMY_HEIGHT / 2, ENEMY_WIDTH, ENEMY_HEIGHT, 8);
    this.add(flash);

    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 200,
      onComplete: () => {
        flash.destroy();
      },
    });

    // Screen shake for 3+ combo
    if (comboCount >= 3) {
      const intensity = Math.min(0.015 + comboCount * 0.003, 0.03);
      this.scene.cameras.main.shake(100 + comboCount * 20, intensity);
    }
  }

  /** Play impact effect when projectile hits */
  playImpactEffect(color: number): void {
    const impact = this.scene.add.graphics();
    impact.fillStyle(color, 0.6);
    impact.fillCircle(0, 0, 25);
    impact.setAlpha(0.8);
    this.add(impact);

    // Expand and fade
    this.scene.tweens.add({
      targets: impact,
      scaleX: 2,
      scaleY: 2,
      alpha: 0,
      duration: 200,
      ease: 'Sine.easeOut',
      onComplete: () => {
        impact.destroy();
      },
    });
  }

  /** Show demand-change visual */
  setDemandChange(phase: 'normal' | 'demand-change-light' | 'demand-change-heavy'): void {
    if (phase === 'normal') {
      this.statusLabel.setVisible(false);
      this.scene.tweens.add({
        targets: this.demandOverlay,
        alpha: 0,
        duration: 200,
      });
      return;
    }

    const isHeavy = phase === 'demand-change-heavy';
    const message = isHeavy
      ? '[DEMAND CHANGE] -25%'
      : '[DEMAND CHANGE] -15%';
    const color = isHeavy ? '#e05545' : '#e6b832';

    this.statusLabel.setText(message);
    this.statusLabel.setColor(color);
    this.statusLabel.setVisible(true);

    // Draw colored border overlay
    this.demandOverlay.clear();
    const overlayColor = isHeavy ? 0xe05545 : 0xe6b832;
    this.demandOverlay.lineStyle(2, overlayColor, 0.8);
    this.demandOverlay.strokeRoundedRect(
      -ENEMY_WIDTH / 2 - 2,
      -ENEMY_HEIGHT / 2 - 2,
      ENEMY_WIDTH + 4,
      ENEMY_HEIGHT + 4,
      10,
    );

    this.scene.tweens.add({
      targets: this.demandOverlay,
      alpha: { from: 0, to: 1 },
      duration: 300,
      ease: 'Sine.easeOut',
    });
  }
}
